import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  useNavigate,
  useLocation,
} from "react-router-dom";

import { API } from "../api";
import Layout from "../components/Layout";

import {
  FiPlus,
  FiEdit,
  FiShield,
  FiCheckCircle,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";


const EMPTY = {
  user_name: "",
  user_loginId: "",
  user_emailId: "",
  user_telno: "",
  user_address: "",
  user_type: "2",
  user_password: "",
  confirmPassword: "",
  mod_ids: [],
};

export default function AddUser() {
  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // USER ID IS PASSED IN BACKGROUND USING ROUTER STATE
  // =====================================================

  const userId = location.state?.userId;

  // EDIT MODE
  const isEdit = Boolean(userId);

  // =====================================================
  // STATE
  // =====================================================

  const [form, setForm] = useState(EMPTY);

  const [allMods, setAllMods] = useState([]);

  const [loading, setLoading] = useState(isEdit);

  const [saving, setSaving] = useState(false);

  // =====================================================
  // TOAST
  // =====================================================

  const toast2 = useCallback(
    (msg, type = "success") => {
      if (type === "error") {
        toasts.error(msg);
      } else if (type === "warning") {
        toasts.warning(msg);
      } else if (type === "info") {
        toasts.info(msg);
      } else {
        toasts.success(msg);
      }
    },
    []
  );

  // =====================================================
  // LOAD DATA
  // =====================================================

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // =========================================
      // LOAD MODULES
      // =========================================

      const modulesResponse =
        await API.get("/users/modules");

      setAllMods(modulesResponse.data || []);

      // =========================================
      // LOAD EDIT USER
      // =========================================

      if (isEdit && userId) {
        const userResponse =
          await API.get(`/users/${userId}`);

        const user = userResponse.data;

        let userModules = [];

        try {
          const moduleResponse =
            await API.get(
              `/users/${userId}/modules`
            );

          userModules = (
            moduleResponse.data || []
          ).map(Number);
        } catch (e) {
          console.error(
            "Failed to load user modules",
            e
          );
        }

        setForm({
          user_name:
            user.user_name || "",

          user_loginId:
            user.user_loginId || "",

          user_emailId:
            user.user_emailId || "",

          user_telno:
            user.user_telno || "",

          user_address:
            user.user_address || "",

          user_type:
            String(user.user_type || 2),

          user_password: "",

          confirmPassword: "",

          mod_ids: userModules,
        });
      }
    } catch (e) {
      console.error(e);

      toast2(
        e.response?.data?.message ||
          "Failed to load user data",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [isEdit, userId, toast2]);

  // =====================================================
  // AUTH + LOAD DATA
  // =====================================================

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
      return;
    }

    loadData();
  }, [navigate, loadData]);

  // =====================================================
  // FIELD CHANGE
  // =====================================================

  const fld = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  // =====================================================
  // EMAIL CHANGE
  // =====================================================

  const handleEmailChange = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      user_emailId: value,
    }));
  };

  // =====================================================
  // MOBILE CHANGE
  // ONLY NUMBERS
  // MAXIMUM 10 DIGITS
  // =====================================================

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    if (value.length <= 10) {
      setForm((prev) => ({
        ...prev,
        user_telno: value,
      }));
    }
  };

  // =====================================================
  // MODULE TOGGLE
  // =====================================================

  const toggleMod = (id) => {
    const moduleId = Number(id);

    setForm((prev) => ({
      ...prev,

      mod_ids: prev.mod_ids.includes(moduleId)
        ? prev.mod_ids.filter(
            (m) => m !== moduleId
          )
        : [
            ...prev.mod_ids,
            moduleId,
          ],
    }));
  };

  // =====================================================
  // SELECT ALL
  // =====================================================

  const selectAll = () => {
    setForm((prev) => ({
      ...prev,

      mod_ids: allMods.map((m) =>
        Number(m.mod_id)
      ),
    }));
  };

  // =====================================================
  // CLEAR ALL
  // =====================================================

  const clearAll = () => {
    setForm((prev) => ({
      ...prev,
      mod_ids: [],
    }));
  };

  // =====================================================
  // SAVE
  // =====================================================

  const handleSave = async () => {
    const email =
      form.user_emailId.trim();

    const mobile =
      form.user_telno.trim();

    // =========================================
    // REQUIRED FIELDS
    // =========================================

    if (
      !form.user_name.trim() ||
      !form.user_loginId.trim() ||
      !email
    ) {
      toast2(
        "Name, Login ID and Email are required",
        "error"
      );

      return;
    }

    // =========================================
    // EMAIL VALIDATION
    // =========================================

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      toast2(
        "Please enter a valid email address",
        "error"
      );

      return;
    }

    // =========================================
    // MOBILE REQUIRED
    // =========================================

    if (!mobile) {
      toast2(
        "Mobile number is required",
        "error"
      );

      return;
    }

    // =========================================
    // MOBILE VALIDATION
    // 10 DIGITS
    // STARTS WITH 6-9
    // =========================================

    if (!/^[6-9]\d{9}$/.test(mobile)) {
      toast2(
        "Mobile number must be a valid 10-digit number",
        "error"
      );

      return;
    }

    // =========================================
    // PASSWORD REQUIRED ONLY FOR ADD
    // =========================================

    if (
      !isEdit &&
      !form.user_password
    ) {
      toast2(
        "Password is required",
        "error"
      );

      return;
    }

    // =========================================
    // PASSWORD MATCHING
    // =========================================

    if (
      form.user_password &&
      form.user_password !==
        form.confirmPassword
    ) {
      toast2(
        "Passwords do not match",
        "error"
      );

      return;
    }

    // =========================================
    // PASSWORD MINIMUM LENGTH
    // =========================================

    if (
      form.user_password &&
      form.user_password.length < 6
    ) {
      toast2(
        "Password must be at least 6 characters",
        "error"
      );

      return;
    }

    setSaving(true);

    try {
      // =========================================
      // EDIT
      // =========================================

      if (isEdit && userId) {
        await API.put(
          `/users/${userId}`,
          form
        );

        toast2(
          "User updated successfully"
        );
      }

      // =========================================
      // ADD
      // =========================================

      else {
        await API.post(
          "/users",
          form
        );

        toast2(
          "User created successfully"
        );
      }

      // =========================================
      // NAVIGATE AFTER TOAST
      // =========================================

      setTimeout(() => {
        navigate("/user-management");
      }, 700);

    } catch (e) {
      console.error(e);

      toast2(
        e.response?.data?.message ||
          "Operation failed",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // ADMIN
  // =====================================================

  const isAdmin =
    form.user_type === "1";

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <Layout activePage="User Management">
        <main className="dashboard-main">
          <div className="user-form-loading">
            Loading user information...
          </div>
        </main>
      </Layout>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <Layout activePage="User Management">

      <main className="dashboard-main">

        <div className="user-form-page">

          {/* HEADER */}

          <div className="user-form-header">

            <div className="d-flex align-items-center gap-2">

              <div className="form-card-icon">

                {isEdit ? (
                  <FiEdit size={24} />
                ) : (
                  <FiPlus size={24} />
                )}

              </div>

              <div>

                <h2 className="um-modal-title mb-0">

                  {isEdit
                    ? "Edit User"
                    : "Add New User"}

                </h2>

                <p className="form-card-sub mb-0">

                  {isEdit
                    ? "(Update user information and module permissions)"
                    : "(Create a new user and set module permissions)"}

                </p>

              </div>

            </div>

            <button
              type="button"
              className="um-btn-secondary"
              onClick={() =>
                navigate("/user-management")
              }
            >
              ← Back to Users
            </button>

          </div>

          {/* FORM CARD */}

          <div className="user-form-card">

            {/* BASIC INFORMATION */}

            <div className="sec-div">
              <span>
                Basic Information
              </span>
            </div>

            <div className="row g-4">

              {/* NAME */}

              <div className="col-xl-3 col-lg-3 col-md-3">

                <label className="um-label">
                  Name *
                </label>

                <input
                  className="um-input"
                  type="text"
                  placeholder="e.g. John Smith"
                  value={form.user_name}
                  onChange={fld(
                    "user_name"
                  )}
                />

              </div>

              {/* LOGIN ID */}

              <div className="col-xl-3 col-lg-3 col-md-3">

                <label className="um-label">
                  User ID *
                </label>

                <input
                  className="um-input"
                  type="text"
                  placeholder="e.g. jsmith"
                  value={
                    form.user_loginId
                  }
                  onChange={fld(
                    "user_loginId"
                  )}
                />

              </div>

              {/* EMAIL */}

              <div className="col-xl-3 col-lg-3 col-md-3">

                <label className="um-label">
                  Email Address *
                </label>

                <input
                  className="um-input"
                  type="email"
                  placeholder="john@example.com"
                  value={
                    form.user_emailId
                  }
                  onChange={
                    handleEmailChange
                  }
                  autoComplete="email"
                />

              </div>

              {/* PHONE */}

              <div className="col-xl-3 col-lg-3 col-md-3">

                <label className="um-label">
                  Telephone No *
                </label>

                <input
                  className="um-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="9876543210"
                  value={
                    form.user_telno
                  }
                  onChange={
                    handleMobileChange
                  }
                  autoComplete="tel"
                />
              </div>

              {/* USER TYPE */}

              <div className="col-xl-4 col-lg-4 col-md-6">

                <label className="um-label">
                  User Type *
                </label>

                <select
                  className="um-select"
                  value={
                    form.user_type
                  }
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,

                      user_type:
                        e.target.value,

                      mod_ids:
                        e.target
                          .value === "1"
                          ? []
                          : prev.mod_ids,
                    }))
                  }
                >

                  <option value="1">
                    Admin User
                  </option>

                  <option value="2">
                    General User
                  </option>

                </select>

              </div>

              {/* PASSWORD */}

              <div className="col-xl-4 col-lg-4 col-md-6">

                <label className="um-label">

                  {isEdit
                    ? "New Password (blank = keep)"
                    : "Password *"}

                </label>

                <input
                  className="um-input"
                  type="password"
                  placeholder="Min 6 characters"
                  value={
                    form.user_password
                  }
                  onChange={fld(
                    "user_password"
                  )}
                />

              </div>

              {/* CONFIRM PASSWORD */}

              <div className="col-xl-4 col-lg-4 col-md-6">

                <label className="um-label">
                  Confirm Password
                </label>

                <input
                  className="um-input"
                  type="password"
                  placeholder="Re-enter password"
                  value={
                    form.confirmPassword
                  }
                  onChange={fld(
                    "confirmPassword"
                  )}
                />

              </div>

            </div>

            {/* MODULE PERMISSIONS */}

            <div className="module-permission-section">

              <div className="sec-divm">

                <span>
                  Module Permissions
                </span>

              </div>

              {/* ADMIN */}

              {isAdmin ? (

                <div className="admin-badge">

                  <span className="admin-badge-icon">
                    <FiShield />
                  </span>

                  <div>

                    <strong>
                      Administrator Access
                    </strong>

                    <div>
                      Admin users automatically
                      receive full access to all{" "}
                      {allMods.length}{" "}
                      modules.
                    </div>

                  </div>

                </div>

              ) : (

                <>

                  {/* TOOLBAR */}

                  <div className="module-toolbar">

                    <span className="module-count">

                      {form.mod_ids.length}{" "}
                      of{" "}
                      {allMods.length}{" "}
                      modules selected

                    </span>

                    <div className="module-actions">

                      <button
                        type="button"
                        className="module-select-btn"
                        onClick={
                          selectAll
                        }
                      >
                        Select All
                      </button>

                      <button
                        type="button"
                        className="module-clear-btn"
                        onClick={
                          clearAll
                        }
                      >
                        Clear All
                      </button>

                    </div>

                  </div>

                  {/* MODULE LIST */}

                  {allMods.length ===
                  0 ? (

                    <div className="no-modules">
                      No modules available
                      in database
                    </div>

                  ) : (

                    <div className="mod-grid">

                      {allMods.map(
                        (mod) => {

                          const checked =
                            form.mod_ids.includes(
                              Number(
                                mod.mod_id
                              )
                            );

                          return (

                            <div
                              key={
                                mod.mod_id
                              }
                              className={`mod-chip ${
                                checked
                                  ? "on"
                                  : ""
                              }`}
                              onClick={() =>
                                toggleMod(
                                  mod.mod_id
                                )
                              }
                            >

                              <div className="chk">

                                {checked
                                  ? ""
                                  : ""}

                              </div>

                              <span>

                                {mod.mod_name ||
                                  mod.mod_title ||
                                  `Module ${mod.mod_id}`}

                              </span>

                            </div>

                          );
                        }
                      )}

                    </div>

                  )}

                </>

              )}

            </div>

            {/* FORM FOOTER */}

            <div className="user-form-footer">

              <button
                type="button"
                className="um-btn-secondary"
                onClick={() =>
                  navigate("/user-management")
                }
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >

                {saving ? (

                  <span className="spinner" />

                ) : isEdit ? (

                  <>
                    <FiCheckCircle />
                    Save Changes
                  </>

                ) : (

                  <>
                    <FiPlus />
                    Create User
                  </>

                )}

              </button>

            </div>

          </div>

        </div>

      </main>

      

    </Layout>
  );
}
